import { SYNC_BYTE_1, SYNC_BYTE_2, HEADER_SIZE, MAX_PAYLOAD_SIZE } from '@/lib/protobuf/utils';

/** Called whenever bytes are discarded as garbage / an invalid frame. */
export type BadFrameHandler = (reason: string, droppedBytes: number) => void;

/** Smallest legal payload: CRC32 only. */
const MIN_PAYLOAD_SIZE = 4;
const MAX_FRAME_SIZE = HEADER_SIZE + MAX_PAYLOAD_SIZE;

/**
 * Reassembles wire frames (`AA 55 <size LE u16> <payload>`) from an arbitrary chunking of the
 * USB IN byte stream (AD-4). One instance per connection. Returns raw payloads
 * (protobuf + CRC32), undecoded; decoding is the caller's job. No timers.
 */
export class FrameAssembler {
  private buffer: Uint8Array = new Uint8Array(0);

  constructor(private readonly onBadFrame?: BadFrameHandler) {}

  /** Number of bytes currently held (never exceeds one max frame). */
  get bufferedLength(): number {
    return this.buffer.length;
  }

  /** Drop any buffered bytes (connect / disconnect / connection loss). */
  reset(): void {
    this.buffer = new Uint8Array(0);
  }

  /** Append `chunk`; return every complete payload now available, in order. */
  feed(chunk: Uint8Array): Uint8Array[] {
    if (chunk.length > 0) {
      const merged = new Uint8Array(this.buffer.length + chunk.length);
      merged.set(this.buffer, 0);
      merged.set(chunk, this.buffer.length);
      this.buffer = merged;
    }

    const payloads: Uint8Array[] = [];
    let pos = 0;
    const buf = this.buffer;

    for (;;) {
      const syncAt = this.findSync(buf, pos);
      if (syncAt < 0) {
        // No sync pair. A trailing 0xAA might be the first half of one; keep only that.
        const keep = buf.length > pos && buf[buf.length - 1] === SYNC_BYTE_1 ? 1 : 0;
        const dropped = buf.length - pos - keep;
        if (dropped > 0) this.onBadFrame?.('no sync bytes', dropped);
        pos = buf.length - keep;
        break;
      }
      if (syncAt > pos) {
        this.onBadFrame?.('garbage before sync bytes', syncAt - pos);
        pos = syncAt;
      }
      if (buf.length - pos < HEADER_SIZE) break; // need the size field

      const size = buf[pos + 2] | (buf[pos + 3] << 8);
      if (size < MIN_PAYLOAD_SIZE) {
        this.onBadFrame?.(`declared payload size ${size} too small for CRC32`, 2);
        pos += 2; // drop the sync bytes, resync
        continue;
      }
      const end = pos + HEADER_SIZE + size;
      if (buf.length < end) break; // incomplete; hold

      payloads.push(buf.slice(pos + HEADER_SIZE, end));
      pos = end;
    }

    this.buffer = pos > 0 ? buf.slice(pos) : buf;

    // Defensive bound: unreachable while the size field is <= 65535, but never grow past it.
    if (this.buffer.length > MAX_FRAME_SIZE) {
      this.onBadFrame?.('buffer exceeded maximum frame size', this.buffer.length);
      this.reset();
    }
    return payloads;
  }

  private findSync(buf: Uint8Array, from: number): number {
    for (let i = from; i + 1 < buf.length; i++) {
      if (buf[i] === SYNC_BYTE_1 && buf[i + 1] === SYNC_BYTE_2) return i;
    }
    return -1;
  }
}
